// Input validation utilities

import type { DayOfWeek } from '@timetable/types'

// Email validation
export function isValidEmail(email: string): boolean {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
  return emailRegex.test(email)
}

// Phone number validation (international format)
export function isValidPhoneNumber(phone: string): boolean {
  // Accepts formats: +1234567890, +12 345 678 90, +12-345-678-90
  const phoneRegex = /^\+?[1-9]\d{1,14}$/
  const cleanPhone = phone.replace(/[\s-]/g, '')
  return phoneRegex.test(cleanPhone)
}

// Period range validation
export function isValidPeriodRange(startTime: string, endTime: string): boolean {
  // Validate HH:MM format
  const timeRegex = /^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/
  if (!timeRegex.test(startTime) || !timeRegex.test(endTime)) {
    return false
  }

  // Parse times
  const [startHour, startMinute] = startTime.split(':').map(Number)
  const [endHour, endMinute] = endTime.split(':').map(Number)

  // Convert to minutes for comparison
  const startMinutes = startHour * 60 + startMinute
  const endMinutes = endHour * 60 + endMinute

  return startMinutes < endMinutes
}

// Check if periods overlap
export function doPeriodsOverlap(
  period1Start: string,
  period1End: string,
  period2Start: string,
  period2End: string
): boolean {
  const [p1StartHour, p1StartMinute] = period1Start.split(':').map(Number)
  const [p1EndHour, p1EndMinute] = period1End.split(':').map(Number)
  const [p2StartHour, p2StartMinute] = period2Start.split(':').map(Number)
  const [p2EndHour, p2EndMinute] = period2End.split(':').map(Number)

  const p1Start = p1StartHour * 60 + p1StartMinute
  const p1End = p1EndHour * 60 + p1EndMinute
  const p2Start = p2StartHour * 60 + p2StartMinute
  const p2End = p2EndHour * 60 + p2EndMinute

  return p1Start < p2End && p2Start < p1End
}

// Validate day of week
export function isValidDayOfWeek(day: string): day is DayOfWeek {
  const validDays: DayOfWeek[] = [
    'MONDAY',
    'TUESDAY',
    'WEDNESDAY',
    'THURSDAY',
    'FRIDAY',
    'SATURDAY',
    'SUNDAY',
  ]
  return validDays.includes(day as DayOfWeek)
}

// Validate period number
export function isValidPeriodNumber(periodNumber: number): boolean {
  return Number.isInteger(periodNumber) && periodNumber >= 0 && periodNumber <= 9
}

// Validate date format (ISO 8601)
export function isValidDate(dateString: string): boolean {
  const date = new Date(dateString)
  return date instanceof Date && !isNaN(date.getTime())
}

// Check if date is in the past
export function isDateInPast(dateString: string): boolean {
  const date = new Date(dateString)
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  return date < today
}

// Validate color code (hex format)
export function isValidColorCode(color: string): boolean {
  const colorRegex = /^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/
  return colorRegex.test(color)
}

// Validate file size (in bytes)
export function isValidFileSize(file: File | Blob, maxSizeInMB: number): boolean {
  const maxSizeInBytes = maxSizeInMB * 1024 * 1024
  return file.size <= maxSizeInBytes
}

// Validate file type
export function isValidFileType(file: File, allowedTypes: string[]): boolean {
  return allowedTypes.includes(file.type)
}

// Validate image file
export function isValidImageFile(file: File, maxSizeInMB: number = 5): boolean {
  const allowedTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/svg+xml']
  return isValidFileType(file, allowedTypes) && isValidFileSize(file, maxSizeInMB)
}

// Validate Excel file
export function isValidExcelFile(file: File, maxSizeInMB: number = 10): boolean {
  const allowedTypes = [
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'application/vnd.ms-excel',
  ]
  return isValidFileType(file, allowedTypes) && isValidFileSize(file, maxSizeInMB)
}

// Password validation constants
export const PASSWORD_RULES = {
  MIN_LENGTH: 6,
  REQUIRE_UPPERCASE: false,
  REQUIRE_LOWERCASE: false,
  REQUIRE_NUMBERS: false,
  REQUIRE_SPECIAL_CHARS: false,
} as const

// Password validation
export function isValidPassword(password: string): boolean {
  return password.length >= PASSWORD_RULES.MIN_LENGTH
}

export function validatePassword(password: string, confirmPassword?: string): ValidationResult {
  const errors: Record<string, string> = {}

  if (!password) {
    errors.password = 'Password is required'
  } else if (password.length < PASSWORD_RULES.MIN_LENGTH) {
    errors.password = `Password must be at least ${PASSWORD_RULES.MIN_LENGTH} characters long`
  }

  if (confirmPassword !== undefined && password !== confirmPassword) {
    errors.confirmPassword = 'Passwords do not match'
  }

  return {
    valid: Object.keys(errors).length === 0,
    errors,
  }
}

// Get password requirements text for UI display
export function getPasswordRequirementsText(): string {
  return `Password must be at least ${PASSWORD_RULES.MIN_LENGTH} characters long`
}

// Generic validation result
export interface ValidationResult {
  valid: boolean
  errors: Record<string, string>
}

// Validate timetable record
export function validateTimetableRecord(record: {
  teacher_id: string
  subject_id: string
  class_section_id: string
  period_id: string
}): ValidationResult {
  const errors: Record<string, string> = {}

  if (!record.teacher_id || record.teacher_id.trim() === '') {
    errors.teacher_id = 'Teacher is required'
  }

  if (!record.subject_id || record.subject_id.trim() === '') {
    errors.subject_id = 'Subject is required'
  }

  if (!record.class_section_id || record.class_section_id.trim() === '') {
    errors.class_section_id = 'Class section is required'
  }

  if (!record.period_id || record.period_id.trim() === '') {
    errors.period_id = 'Period is required'
  }

  return {
    valid: Object.keys(errors).length === 0,
    errors,
  }
}

// Validate teacher
export function validateTeacher(teacher: {
  name: string
  email?: string
  phone_number?: string
  teacher_type_id: string
}): ValidationResult {
  const errors: Record<string, string> = {}

  if (!teacher.name || teacher.name.trim() === '') {
    errors.name = 'Name is required'
  }

  if (teacher.email && !isValidEmail(teacher.email)) {
    errors.email = 'Invalid email format'
  }

  if (teacher.phone_number && !isValidPhoneNumber(teacher.phone_number)) {
    errors.phone_number = 'Invalid phone number format'
  }

  if (!teacher.teacher_type_id || teacher.teacher_type_id.trim() === '') {
    errors.teacher_type_id = 'Teacher type is required'
  }

  return {
    valid: Object.keys(errors).length === 0,
    errors,
  }
}

// Validate subject
export function validateSubject(subject: {
  subject_name: string
  subject_code?: string
  color_code?: string
}): ValidationResult {
  const errors: Record<string, string> = {}

  if (!subject.subject_name || subject.subject_name.trim() === '') {
    errors.subject_name = 'Subject name is required'
  }

  if (subject.color_code && !isValidColorCode(subject.color_code)) {
    errors.color_code = 'Invalid color code format (use #RRGGBB)'
  }

  return {
    valid: Object.keys(errors).length === 0,
    errors,
  }
}

// Validate period
export function validatePeriod(period: {
  day: string
  period_number: number
  time_start: string
  time_end: string
  is_break?: boolean
  break_type?: 'short' | 'lunch' | 'recess' | null
}): ValidationResult {
  const errors: Record<string, string> = {}

  if (!isValidDayOfWeek(period.day)) {
    errors.day = 'Please select a valid day of the week (Monday through Sunday)'
  }

  if (!isValidPeriodNumber(period.period_number)) {
    errors.period_number = 'Period number must be between 0 and 9. Please enter a valid number.'
  }

  if (!isValidPeriodRange(period.time_start, period.time_end)) {
    errors.time_range = 'The start time must be before the end time. Please adjust your times.'
  }

  // Validate break period fields
  if (period.is_break === true) {
    if (!period.break_type) {
      errors.break_type = 'Please select a break type (Short Break, Lunch Break, or Recess) for this break period'
    } else if (!['short', 'lunch', 'recess'].includes(period.break_type)) {
      errors.break_type = 'Break type must be one of: short, lunch, recess'
    }
  }

  // Ensure break_type is not set when is_break is false
  if (period.is_break === false && period.break_type) {
    errors.break_type = 'Break type should not be set for non-break periods'
  }

  return {
    valid: Object.keys(errors).length === 0,
    errors,
  }
}

// Validate absence
export function validateAbsence(absence: {
  teacher_id: string
  date: string
  absence_type: string
  start_time?: string
  end_time?: string
}): ValidationResult {
  const errors: Record<string, string> = {}

  if (!absence.teacher_id || absence.teacher_id.trim() === '') {
    errors.teacher_id = 'Teacher is required'
  }

  if (!isValidDate(absence.date)) {
    errors.date = 'Invalid date format'
  }

  if (isDateInPast(absence.date)) {
    errors.date = 'Date cannot be in the past'
  }

  if (!['full_day', 'temporary_duty'].includes(absence.absence_type)) {
    errors.absence_type = 'Invalid absence type'
  }

  if (absence.absence_type === 'temporary_duty') {
    if (!absence.start_time || !absence.end_time) {
      errors.time_range = 'Start and end time are required for temporary duty'
    } else if (!isValidPeriodRange(absence.start_time, absence.end_time)) {
      errors.time_range = 'Start time must be before end time'
    }
  }

  return {
    valid: Object.keys(errors).length === 0,
    errors,
  }
}
